import { FC, useState } from 'react';
import {
  useDeleteCompanyMutation,
  useEditCompanyMutation,
} from '../../../../../api/companyApi';
import MenuButton from '../../../../../components/common/MenuButton';
import PolicyRequired from '../../../../../components/common/PolicyRequired';
import { Company } from '../../../../../types/companies';
import { CompanyFormDataProviderProps } from '../../EditSideBar';
import { logger } from '../../../../../services/logger';
import { formatTime } from '../../../../../services/formatTime';
import { AlertDialog } from '../../../../../components/common/AlertDialog';

type Props = {
  company: Company;
  openEdit: (props: CompanyFormDataProviderProps | null) => void;
  sidebarClose: () => void;
};
const TableMenuButton: FC<Props> = ({ company, openEdit, sidebarClose }) => {
  const [deleteCompany] = useDeleteCompanyMutation();
  const [editCompany] = useEditCompanyMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const onClose = () => {
    closeModal();
  };
  const onDelete = async () => {
    await deleteCompany(company.companyId);
    onClose();
  };
  const confirmModal = company.isLinkedToContract ? (
    <AlertDialog
      title="受注入力データに登録されています"
      description={
        <>
          <div>受注入力データに配車情報として利用されています。</div>
          <div>
            削除すると配車計算に影響が出る可能性があります。本当に削除しますか？
          </div>
        </>
      }
      onClose={onClose}
      onConfirm={onDelete}
      variant={['error', 'withCancel']}
    />
  ) : (
    <AlertDialog
      title="本当に削除しますか？"
      description="削除すると、元に戻すことはできません。"
      onClose={onClose}
      onConfirm={onDelete}
      variant={['withCancel']}
    />
  );

  return (
    <>
      <MenuButton>
        {({ close, itemClassName }) => (
          <>
            <PolicyRequired policy={25}>
              <button
                type="button"
                className={itemClassName}
                onClick={() => {
                  openEdit({
                    formData: {
                      companyName: company.companyName,
                      postCode: company.postCode,
                      prefectureId: company.prefecture.id,
                      phoneNumber: company.phoneNumber,
                      address1: company.address1,
                      address2: company.address2,
                      address3: company.address3 ?? '',
                      companyTypeId: company.companyType.id,
                      businessHoursStartAt: formatTime(
                        company.businessHoursStartAt
                      ),
                      businessHoursEndAt: formatTime(
                        company.businessHoursEndAt
                      ),
                      businessTypeId: company.businessType?.id || 1,
                      companyWasteItemIds: (
                        company.companyWasteItems ?? []
                      ).map((i) => i.id),
                    },
                    type: 'edit',
                    onSubmit(data) {
                      // 会社区分がよって必要のないデータを除去する
                      if (data.companyTypeId === /* 顧客 */ 2) {
                        delete data.companyWasteItemIds;
                      } else if (data.companyTypeId === /* 処分事業者 */ 3) {
                        delete data.businessTypeId;
                      }

                      editCompany({ id: company.companyId, data })
                        .unwrap()
                        .then(() => {
                          sidebarClose();
                        })
                        .catch(logger.error);
                    },
                  });
                  close();
                }}
              >
                編集
              </button>
            </PolicyRequired>
            <PolicyRequired policy={26}>
              <button
                type="button"
                className={itemClassName}
                onClick={() => {
                  openModal();
                  close();
                }}
              >
                削除
              </button>
            </PolicyRequired>
          </>
        )}
      </MenuButton>
      {modalOpen && confirmModal}
    </>
  );
};

export default TableMenuButton;
